import { TFunction } from 'react-i18next';

import {
  CreatePromoCodeRequest,
  PromoCode,
  PromoCodeDraft,
  PromoCodeErrors,
  UpdatePromoCodeRequest,
} from '../../../stores/types/promoCode.interface';
import { Promotion } from '../../../stores/types/promotions.interface';
import { toValidDate } from '../promotion.utils';
import {
  DEFAULT_PROMO_CODE_ACCESS_TYPE,
  DEFAULT_PROMO_CODE_USAGE_LIMIT_TYPE,
  PROMO_CODE_MAX_LENGTH,
  PROMO_CODE_MIN_LENGTH,
} from './promoCode.constants';

export const hasMinLength = (code: string): boolean => code.trim().length >= PROMO_CODE_MIN_LENGTH;

export const hasMaxLength = (code: string): boolean => code.trim().length <= PROMO_CODE_MAX_LENGTH;

/** A code passes live validation when it is within the allowed length range. */
export const isCodeValid = (code: string): boolean => hasMinLength(code) && hasMaxLength(code);

const isWithinPromotionWindow = (date: Date, promotion: Promotion): boolean => {
  const promotionStart = toValidDate(promotion.validFrom);
  const promotionEnd = toValidDate(promotion.validUntil);
  if (promotionStart && date < promotionStart) return false;
  if (promotionEnd && date > promotionEnd) return false;
  return true;
};

export const validatePromoCodeDraft = (
  draft: PromoCodeDraft | PromoCode,
  promotion: Promotion,
  t: TFunction,
): PromoCodeErrors => {
  const errors: PromoCodeErrors = {};

  const code = draft.code.trim();
  if (!code) {
    errors.code = t('promo_code_required_error');
  } else if (!isCodeValid(code)) {
    errors.code = t('promo_code_invalid_error');
  }

  const validFromDate = toValidDate(draft.validFrom);
  const validUntilDate = toValidDate(draft.validUntil);

  if (!validFromDate) {
    errors.validFrom = t('required_field');
  } else if (!isWithinPromotionWindow(validFromDate, promotion)) {
    errors.validFrom = t('invalid_start_date');
  }

  if (validUntilDate) {
    const beforeStart = validFromDate && validUntilDate < validFromDate;
    if (beforeStart || !isWithinPromotionWindow(validUntilDate, promotion)) {
      errors.validUntil = t('invalid_end_date');
    } else {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      if (validUntilDate < now) {
        errors.validUntil = t('end_date_in_past');
      }
    }
  }

  return errors;
};

export const hasPromoCodeErrors = (errors: PromoCodeErrors): boolean =>
  Boolean(errors.code || errors.validFrom || errors.validUntil);

export const buildCreatePromoCodePayload = (
  draft: PromoCodeDraft,
  promotionId: string,
): CreatePromoCodeRequest => ({
  code: draft.code.trim(),
  promotionId,
  accessType: DEFAULT_PROMO_CODE_ACCESS_TYPE,
  usageLimitType: DEFAULT_PROMO_CODE_USAGE_LIMIT_TYPE,
  validFrom: draft.validFrom ? draft.validFrom.toISOString() : null,
  validUntil: draft.validUntil ? draft.validUntil.toISOString() : null,
  allowedUsers: null,
  usesPerCustomer: null,
  totalUsesLimit: null,
});

/**
 * Builds the PATCH payload for editing a DRAFT code. Only the fields exposed in the
 * edit form (code + dates) are sent; access/usage settings are left untouched.
 */
export const buildUpdatePromoCodePayload = (draft: PromoCodeDraft): UpdatePromoCodeRequest => ({
  code: draft.code.trim(),
  validFrom: draft.validFrom ? draft.validFrom.toISOString() : null,
  validUntil: draft.validUntil ? draft.validUntil.toISOString() : null,
});
